import { sql } from "drizzle-orm";
import { pgTable, varchar, uuid, text, timestamp, boolean, pgEnum, jsonb } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

// ============================================================================
// SECURITY & MULTI-TENANCY MODULE (Sprint 27)
// ============================================================================

// User Role Enum (8 roles per PRD permission matrix)
export const userRoleEnum = pgEnum("user_role_enum", [
  "system_admin",
  "org_owner",
  "compliance_officer",
  "admin",
  "manager",
  "broker",
  "support",
  "readonly",
]);

// Organisation Status Enum
export const orgStatusEnum = pgEnum("org_status_enum", [
  "active",
  "suspended",
  "cancelled",
]);

// Organisation Plan Type Enum
export const orgPlanTypeEnum = pgEnum("org_plan_type_enum", [
  "free",
  "starter",
  "professional",
  "enterprise",
]);

// User Profile Status Enum
export const userProfileStatusEnum = pgEnum("user_profile_status_enum", [
  "active",
  "suspended",
  "deactivated",
]);

// 1. Organisations Table
export const organisations = pgTable("crm_x_organisations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 255 }).notNull(),
  slug: varchar("slug", { length: 255 }).notNull(),
  ownerId: uuid("owner_id"),
  planType: orgPlanTypeEnum("plan_type").default("professional"),
  status: orgStatusEnum("status").default("active"),
  isCanary: boolean("is_canary").notNull().default(false),
  metadata: jsonb("metadata").default(sql`'{}'::jsonb`),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

// 2. User Profiles Table
export const userProfiles = pgTable("crm_x_user_profiles", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: text("user_id").notNull(),
  organisationId: uuid("organisation_id").references(() => organisations.id, { onDelete: "cascade" }),
  role: userRoleEnum("role").notNull().default("broker"),
  displayName: varchar("display_name", { length: 255 }),
  status: userProfileStatusEnum("status").default("active"),
  isCanary: boolean("is_canary").default(false).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

// 3. Role Permissions Reference Table
export const rolePermissions = pgTable("crm_x_role_permissions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  role: userRoleEnum("role").notNull(),
  entityType: varchar("entity_type", { length: 100 }).notNull(),
  canCreate: boolean("can_create").default(false).notNull(),
  canRead: boolean("can_read").default(false).notNull(),
  canUpdate: boolean("can_update").default(false).notNull(),
  canDelete: boolean("can_delete").default(false).notNull(),
  conditions: jsonb("conditions").default(sql`'{}'::jsonb`),
});

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

export const insertOrganisationSchema = createInsertSchema(organisations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const updateOrganisationSchema = insertOrganisationSchema.partial();
export const selectOrganisationSchema = createSelectSchema(organisations);

export const insertUserProfileSchema = createInsertSchema(userProfiles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const updateUserProfileSchema = insertUserProfileSchema.partial();
export const selectUserProfileSchema = createSelectSchema(userProfiles);

export const insertRolePermissionSchema = createInsertSchema(rolePermissions).omit({
  id: true,
});
export const selectRolePermissionSchema = createSelectSchema(rolePermissions);

// ============================================================================
// TYPESCRIPT TYPES
// ============================================================================

export type InsertOrganisation = z.infer<typeof insertOrganisationSchema>;
export type UpdateOrganisation = z.infer<typeof updateOrganisationSchema>;
export type Organisation = typeof organisations.$inferSelect;

export type InsertUserProfile = z.infer<typeof insertUserProfileSchema>;
export type UpdateUserProfile = z.infer<typeof updateUserProfileSchema>;
export type UserProfile = typeof userProfiles.$inferSelect;

export type InsertRolePermission = z.infer<typeof insertRolePermissionSchema>;
export type RolePermission = typeof rolePermissions.$inferSelect;
